/**
 * L'avís de preu del formulari — plans/preus-en-crear-reserves.md, fase F2.
 *
 * Pur: donat el preu escrit i el tipus d'IVA, diu si cal avisar i quins dos preus bons té al
 * costat. La regla és la de `@guilleumes/types` (`vat-split.ts`); aquí no se'n reescriu res.
 *
 * Decisions d'en Pere que mana aquest fitxer:
 * - 1 (11-OCT-2026): s'avisa i es deixa desar; mai es bloqueja ni s'arrodoneix sol. L'avís ensenya
 *   el preu bo més proper per sota i per sobre, i qui crea en tria un o l'ignora.
 * - 2 (12-OCT-2026): l'estat B («exacte només com a total») s'accepta sense soroll. Només l'estat C
 *   («sense base exacta») porta avís. Per tant 84,48 € (A) i 84,50 € (B) no ensenyen res.
 * - 12 (12-OCT-2026): només en crear i en editar un preu; cap avís per un preu que ja hi era
 *   (`priceVatNoticeForEdit`).
 *
 * Tot en cèntims enters: l'única conversió des d'euros és `toCents`.
 */

import { nearestOnGrid, toCents, vatRateFromPercent } from '@guilleumes/types';

export interface PriceVatNotice {
  /** El preu escrit, en cèntims. */
  grossCents: number;
  /** El tipus aplicat, en centèsimes de punt (10 % = 1000). */
  rateBasisPoints: number;
  /** El preu amb base exacta més proper per sota, en cèntims; `null` si no n'hi ha cap de més gran que 0. */
  belowCents: number | null;
  /** El preu amb base exacta més proper per sobre, en cèntims; `null` si passaria del sostre. */
  aboveCents: number | null;
}

/**
 * L'avís que toca per a un preu en euros, o `null` si no cal dir res.
 *
 * `null` quan: el preu és buit, no és un número, és 0 o negatiu; té més de 2 decimals (això ho diu
 * la validació del camp i ho rebutja el servei, no aquest avís); el tipus no és vàlid; o el preu és
 * en estat A o B. No llança mai: es pot cridar dins d'un render.
 */
export function priceVatNotice(price: unknown, vatPercent: unknown): PriceVatNotice | null {
  const cents = toCents(price);
  if (cents.ok === false || cents.cents === 0 || !cents.exact) return null;
  const rate = vatRateFromPercent(vatPercent);
  if (rate.ok === false) return null;

  const neighbours = nearestOnGrid(cents.cents, rate.rate);
  if (neighbours.state !== 'C') return null;

  const below = neighbours.exactBase.below?.grossCents ?? null;
  return {
    grossCents: cents.cents,
    rateBasisPoints: neighbours.rateBasisPoints,
    belowCents: below !== null && below > 0 ? below : null,
    aboveCents: neighbours.exactBase.above?.grossCents ?? null,
  };
}

/**
 * El mateix, per als llocs on s'edita una reserva que ja existeix: només hi ha avís si el preu
 * escrit és un preu NOU (diferent del que la reserva ja té). Un preu antic que no es toca no
 * avisa, encara que sigui en estat C (decisió 12).
 */
export function priceVatNoticeForEdit(
  newPrice: number | null | undefined,
  currentPrice: number | null | undefined,
  vatPercent: unknown,
): PriceVatNotice | null {
  if (newPrice == null || newPrice === (currentPrice ?? null)) return null;
  return priceVatNotice(newPrice, vatPercent);
}

/**
 * `true` si un preu en euros té més de 2 decimals de debò (84.505). Buit o no numèric: `false`.
 * Un import que no és 0 però no arriba a un cèntim (0.0000001) també en té: `toCents` el donaria
 * per un 0 exacte, i el servei el rebutja igual.
 */
export function hasMoreThanTwoDecimals(price: unknown): boolean {
  const cents = toCents(price);
  if (cents.ok === false) return false;
  return !cents.exact || (cents.cents === 0 && price !== 0);
}

// ─── Presentació (enters, sense coma flotant) ────────────────────────────────

function splitCents(cents: number): { euros: number; rest: string } {
  const rest = cents % 100;
  return { euros: (cents - rest) / 100, rest: String(rest).padStart(2, '0') };
}

/** `9717` → `97,17 €`. */
export function formatCents(cents: number): string {
  const { euros, rest } = splitCents(cents);
  return `${euros},${rest} €`;
}

/** `9717` → `97.17`: el valor que entén un `<input type="number">`. */
export function centsToInputValue(cents: number): string {
  const { euros, rest } = splitCents(cents);
  return `${euros}.${rest}`;
}

/** `1000` → `10 %`; `520` → `5,2 %`; `525` → `5,25 %`. */
export function formatVatRate(rateBasisPoints: number): string {
  const { euros: whole, rest } = splitCents(rateBasisPoints);
  const decimals = rest.replace(/0+$/, '');
  return `${whole}${decimals ? `,${decimals}` : ''} %`;
}

/** La frase de l'avís: «Amb l'IVA del 10 %, 97,18 € no quadra al cèntim.» */
export function priceVatNoticeText(notice: PriceVatNotice): string {
  return `Amb l'IVA del ${formatVatRate(notice.rateBasisPoints)}, ${formatCents(notice.grossCents)} no quadra al cèntim.`;
}

/** La segona línia: el preu es pot desar igualment. */
export const PRICE_VAT_NOTICE_HINT = 'Pots desar-lo igualment o triar-ne un de proper:';
